import { SearchInput } from '@mm/components';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { selectAccessiblePagesByRoles } from '@mm/state/selectors/facebookPageSelector';
import { selectCompanies } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CheckBox from '../../../../../../../../common/components/Checkbox';
import styles from './ConnectedPagesSelector.module.css';

type Props = {
  className?: string;
  selectedPageIds: number[];
  setSelectedPageIds: React.Dispatch<React.SetStateAction<number[]>>;
};

export const ConnectedPagesSelector = (props: Props) => {
  const { selectedPageIds, setSelectedPageIds, className } = props;
  const { fetchAccessiblePagesByRole } = useConnectedPageCRUDActions();
  const companies = useSelector(selectCompanies);
  const accessiblePages = useSelector(selectAccessiblePagesByRoles);
  const [filteredPages, setFilteredPages] = useState(accessiblePages);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setFilteredPages(accessiblePages);
  }, [accessiblePages]);

  useEffect(() => {
    fetchAccessiblePagesByRole();
  }, [fetchAccessiblePagesByRole, companies]);

  const onSelect = (pageId: number) => {
    const match = selectedPageIds.find(id => id === pageId);
    if (!match) {
      return setSelectedPageIds([...selectedPageIds, pageId]);
    }

    const up = selectedPageIds.filter(id => id !== pageId);
    return setSelectedPageIds(up);
  };

  const onSearch = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(value.toLocaleLowerCase());
    setFilteredPages(
      accessiblePages?.filter(page =>
        page.name?.toLocaleLowerCase().includes(value.toLocaleLowerCase()),
      ),
    );
  };

  return (
    <div className={classNames(styles.container, className)}>
      <SearchInput
        placeholder="Search a page"
        value={search}
        onChange={onSearch}
        css={{ container: styles.searchInput, input: styles.input }}
      />
      <div className={styles.content}>
        {companies.map(company => {
          return (
            <div key={company.id} className={styles.block}>
              <div
                className={classNames(styles.heading, {
                  [styles.hiddenHeading]: !filteredPages?.filter(
                    page => page.companyId === company.id,
                  ).length,
                })}
                key={company.id}
              >
                {company.name}
              </div>

              {filteredPages
                ?.filter(page => page.companyId === company.id)
                .map(page => (
                  <CheckBox
                    label={page.name?.toString()}
                    key={page.id.toString()}
                    isChecked={!!selectedPageIds?.find(id => id === page.id)}
                    className={styles.checkbox}
                    onClick={() => onSelect(page.id)}
                    disabled={
                      selectedPageIds.length >= 3 &&
                      Boolean(!selectedPageIds?.includes(page.id))
                    }
                  />
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
